const GA_MEASUREMENT_ID = 'G-6J439RCQ4P';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

export function initGA() {
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID);
}

export function disableGA() {
  (window as any)[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
}

export function enableGA() {
  (window as any)[`ga-disable-${GA_MEASUREMENT_ID}`] = false;
}

export function checkAndInitGA() {
  const consent = localStorage.getItem('cookie-consent');
  if (consent === 'accepted') {
    enableGA();
    initGA();
  } else {
    disableGA();
  }
}

// ============================================================
// E-commerce + engagement event helpers (GA4 standard names)
// All helpers are no-ops if gtag isn't loaded (e.g. consent not given)
// ============================================================

type AnyProduct = any;
type AnyVariant = any;

function safeGtag(...args: any[]) {
  if (typeof window === 'undefined') return;
  if (typeof window.gtag !== 'function') return;
  try {
    window.gtag(...args);
  } catch (e) {
    console.warn('gtag error', e);
  }
}

function getNode(p: AnyProduct) {
  return p?.node ? p.node : p;
}

function getCategory(p: AnyProduct): string | undefined {
  const n = getNode(p);
  return n?.productType || n?.product_type || n?.tags?.[0];
}

function buildItem(product: AnyProduct, variant?: AnyVariant, quantity = 1) {
  const n = getNode(product);
  const price = variant?.price?.amount
    ? parseFloat(variant.price.amount)
    : n?.priceRange?.minVariantPrice?.amount
    ? parseFloat(n.priceRange.minVariantPrice.amount)
    : 0;
  return {
    item_id: variant?.sku || variant?.id || n?.handle || n?.id,
    item_name: n?.title,
    item_brand: n?.vendor,
    item_category: getCategory(product),
    item_variant: variant?.title,
    price,
    quantity,
  };
}

export function trackViewItem(product: AnyProduct, variant?: AnyVariant) {
  const item = buildItem(product, variant, 1);
  safeGtag('event', 'view_item', {
    currency: 'EUR',
    value: item.price,
    items: [item],
  });
}

export function trackAddToCart(product: AnyProduct, variant: AnyVariant, quantity = 1) {
  const item = buildItem(product, variant, quantity);
  safeGtag('event', 'add_to_cart', {
    currency: 'EUR',
    value: item.price * quantity,
    items: [item],
  });
}

export function trackRemoveFromCart(item: any) {
  const built = buildItem(item.product, { id: item.variantId, sku: item.product?.node?.variants?.edges?.find?.((e: any) => e.node.id === item.variantId)?.node?.sku, title: item.variantTitle, price: item.price }, item.quantity);
  safeGtag('event', 'remove_from_cart', {
    currency: 'EUR',
    value: built.price * item.quantity,
    items: [built],
  });
}

function cartItemsToGa(items: any[]) {
  return items.map((it) =>
    buildItem(it.product, { id: it.variantId, title: it.variantTitle, price: it.price }, it.quantity)
  );
}

export function trackViewCart(items: any[], total: number) {
  if (!items?.length) return;
  safeGtag('event', 'view_cart', {
    currency: 'EUR',
    value: total,
    items: cartItemsToGa(items),
  });
}

export function trackBeginCheckout(items: any[], total: number) {
  if (!items?.length) return;
  safeGtag('event', 'begin_checkout', {
    currency: 'EUR',
    value: total,
    items: cartItemsToGa(items),
  });
}

export function trackAddToWishlist(product: AnyProduct, variant?: AnyVariant) {
  const item = buildItem(product, variant, 1);
  safeGtag('event', 'add_to_wishlist', {
    currency: 'EUR',
    value: item.price,
    items: [item],
  });
}
